import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { API_BASE, page, cardScroll, btn, btnDisabled, ConfirmDialog, navigateUnlessTextSelected } from './AdminUsers';
import { Link } from 'react-router-dom';
import TopBanner from '../components/TopBanner';
import AdminSidebar from '../components/AdminSidebar';

// Every self-registered account now needs a bescom-super-admins sign-off
// before it gets real dashboard/app access — see profile.js (defaults
// attributes.approval_status to 'pending' on first profile completion)
// and Dashboard.jsx (shows a waiting screen instead of the app grid while
// pending). This page is where that sign-off happens.
//
// UPDATED 2026-08-05: restyled onto the same light glass-card shell as
// AdminUsers.jsx/User Directory (page/card/btn/btnDisabled imported from
// there) — this used to have its own bespoke dark "mirror" glass look,
// inconsistent with the rest of the admin section.

const rowBg = '#fff';
const rowBorder = '1px solid #eee';

function posting(u) {
  return [u.zone, u.circle, u.division, u.subdivision, u.section].filter(Boolean).join(' → ') || '—';
}

export default function AdminApprovals() {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();

  const [pending, setPending] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actingPk, setActingPk] = useState(null);
  const [error, setError] = useState(null);
  const [dialog, setDialog] = useState(null);

  // Guard: only bescom-super-admins. Two cases, not one — used to only
  // check the group when `user` was already truthy, which silently did
  // nothing for a visitor with NO session at all (this used to be masked
  // by a separate localStorage-token check, removed as part of the
  // httpOnly-cookie migration — see AuthContext.js's own header comment —
  // so this now has to cover both cases itself).
  useEffect(() => {
    if (authLoading) return;
    if (!user) {
      navigate('/');
      return;
    }
    const groups = user.profile?.groups || [];
    if (!groups.includes('bescom-super-admins')) {
      navigate('/dashboard');
    }
  }, [user, authLoading, navigate]);

  // Phase 2 of the httpOnly-cookie migration (see AuthContext.js's own
  // header comment) — no more synchronous "is there a token" pre-check
  // possible. fetchPending()'s own 401 handling below does the job now.
  useEffect(() => {
    fetchPending();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function fetchPending() {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`${API_BASE}/admin/approvals`, {
        credentials: 'include',
      });
      if (res.status === 401) {
        navigate('/', { state: { sessionExpired: true } });
        return;
      }
      if (res.status === 403) {
        setError('You are not authorized to access this page.');
        return;
      }
      const data = await res.json();
      setPending(data.results || []);
    } catch (e) {
      setError('Could not reach the identity service.');
    } finally {
      setLoading(false);
    }
  }

  async function runAction(pk, action, failureMessage) {
    setActingPk(pk);
    setError(null);
    try {
      const res = await fetch(`${API_BASE}/admin/approvals/${pk}/${action}`, {
        method: 'POST',
        credentials: 'include',
      });
      const data = await res.json();
      if (!res.ok) {
        setError(data.error || failureMessage);
        return;
      }
      setPending((prev) => prev.filter((u) => u.pk !== pk));
    } catch (e) {
      setError('Could not reach the identity service.');
    } finally {
      setActingPk(null);
    }
  }

  function confirmApprove(u) {
    setDialog({
      title: 'Approve registration',
      message: `Approve ${u.name || u.username}? They'll get real dashboard/app access right away.`,
      confirmLabel: 'Approve',
      onConfirm: () => {
        setDialog(null);
        runAction(u.pk, 'approve', 'Could not approve this user.');
      },
    });
  }

  function confirmReject(u) {
    setDialog({
      title: 'Reject registration',
      message: `Reject ${u.name || u.username}? Their account will be deactivated — they won't be able to log in unless a super admin reactivates it later.`,
      confirmLabel: 'Reject',
      danger: true,
      onConfirm: () => {
        setDialog(null);
        runAction(u.pk, 'reject', 'Could not reject this user.');
      },
    });
  }

  return (
    <>
      <TopBanner />
      <div className="admin-page-wrap" style={page}>
      <div className="admin-row" style={{ display: 'flex', gap: '24px', width: '100%', height: '100%' }}>
      <AdminSidebar active="approvals" />
      <div style={cardScroll}>
        <h1 style={{ fontSize: '20px', fontWeight: '700', color: '#1a1a1a', margin: '0 0 4px' }}>
          Pending Registrations
        </h1>
        <p style={{ fontSize: '13px', color: '#666', marginBottom: '24px' }}>
          These accounts finished registration and profile setup but won't see their applications until approved here.
        </p>

        {error && (
          <div style={{ background: '#FDECEC', color: '#B3261E', padding: '10px 14px', borderRadius: '8px', fontSize: '13px', marginBottom: '16px' }}>
            {error}
          </div>
        )}

        {loading ? (
          <p style={{ textAlign: 'center', color: '#666', fontSize: '14px' }}>Loading…</p>
        ) : pending.length === 0 ? (
          <div style={{ textAlign: 'center', color: '#666', padding: '40px' }}>
            <p style={{ fontSize: '32px', margin: '0 0 16px' }}>✅</p>
            <p>Nothing pending — all caught up.</p>
          </div>
        ) : (
          <div style={{ border: '1px solid #eee', borderRadius: '10px', overflow: 'hidden' }}>
            {pending.map((u) => {
              const userPath = `/admin/users/${u.pk}`;
              return (
                <div
                  key={u.pk}
                  onClick={navigateUnlessTextSelected(navigate, userPath)}
                  className="admin-row-hover"
                  style={{
                    padding: '14px 16px', background: rowBg, borderBottom: rowBorder, cursor: 'pointer',
                    display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '16px',
                  }}
                >
                  <div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                      {/* Only the name/username is a real link — right-click/
                          middle-click "open in new tab" works here; the rest
                          of the row stays plain selectable/copyable text,
                          with a normal click anywhere navigating via the
                          row's own onClick above. Same trade-off as
                          AdminUsers.jsx's own DirectoryTable. */}
                      <span onClick={(e) => e.stopPropagation()}>
                        <Link to={userPath} style={{ fontWeight: '600', fontSize: '14px', color: '#1a1a1a' }}>
                          {u.name || u.username}
                        </Link>
                      </span>
                      {(u.reasons || []).map((reason) => (
                        <span
                          key={reason}
                          style={{ fontSize: '11px', fontWeight: '600', color: '#0072B1', background: '#E6F5FC', padding: '2px 8px', borderRadius: '10px' }}
                        >
                          {reason}
                        </span>
                      ))}
                    </div>
                    <div style={{ fontSize: '12px', color: '#666', marginTop: '2px' }}>
                      {u.username} · {u.email || 'no email'} · {u.phone || 'no phone'}
                    </div>
                    <div style={{ fontSize: '12px', color: '#666', marginTop: '2px' }}>
                      {u.designation || 'No designation'} — {posting(u)}
                    </div>
                  </div>
                  <div style={{ display: 'flex', gap: '8px', flexShrink: 0 }}>
                    <button
                      onClick={(e) => { e.stopPropagation(); confirmApprove(u); }}
                      disabled={actingPk === u.pk}
                      style={actingPk === u.pk ? btnDisabled : btn}
                    >
                      {actingPk === u.pk ? 'Working…' : 'Approve'}
                    </button>
                    <button
                      onClick={(e) => { e.stopPropagation(); confirmReject(u); }}
                      disabled={actingPk === u.pk}
                      style={actingPk === u.pk
                        ? btnDisabled
                        : { background: '#B3261E', border: 'none', color: '#fff', borderRadius: '8px', fontSize: '14px', fontWeight: '600', padding: '10px 20px', cursor: 'pointer' }}
                    >
                      Reject
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
      </div>
      </div>
      <ConfirmDialog dialog={dialog} onCancel={() => setDialog(null)} />
    </>
  );
}
